import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Detects Word/Office-specific markup in HTML
 */
function isWordContent(html) {
  return (
    html.includes('urn:schemas-microsoft-com') ||
    html.includes('mso-') ||
    html.includes('MsoNormal') ||
    html.includes('<o:p>') ||
    html.includes('class="Mso')
  );
}

/**
 * Clean HTML from Word/external sources before pasting.
 * Ported from the original Quill-based RichTextEditor.
 */
function cleanHtmlForPaste(html) {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // 1. Remove non-content elements
  temp.querySelectorAll('style, meta, link, title, xml').forEach(el => el.remove());

  // 2. Remove HTML comments
  const removeComments = (node) => {
    [...node.childNodes].forEach(child => {
      if (child.nodeType === 8) child.remove();
      else if (child.childNodes && child.childNodes.length > 0) removeComments(child);
    });
  };
  removeComments(temp);

  // 3. Remove Word XML namespace tags (o:p, v:*, w:*, m:*)
  temp.querySelectorAll('o\\:p').forEach(el => {
    el.replaceWith(document.createTextNode(el.textContent || ''));
  });

  // 4. Convert Word list paragraphs to proper <ol>/<ul>/<li>
  const isWordListParagraph = (el) => {
    if (el.tagName !== 'P') return false;
    const cls = el.className || '';
    const style = el.getAttribute('style') || '';
    return cls.includes('MsoListParagraph') || (style.includes('mso-list:') && style.includes('level'));
  };

  const getListLevel = (el) => {
    const style = el.getAttribute('style') || '';
    const levelMatch = style.match(/level(\d+)/);
    if (levelMatch) return parseInt(levelMatch[1], 10);
    const marginMatch = style.match(/margin-left:\s*([\d.]+)pt/);
    if (marginMatch) return Math.max(1, Math.round(parseFloat(marginMatch[1]) / 36));
    return 1;
  };

  const isOrderedItem = (el) => {
    const spans = el.querySelectorAll('span');
    for (const span of spans) {
      const style = span.getAttribute('style') || '';
      if (style.includes('mso-list') && style.includes('Ignore')) {
        const text = span.textContent.trim();
        if (/^\d+[.)]\s*$/.test(text) || /^[a-zA-Z][.)]\s*$/.test(text)) return true;
        return false;
      }
    }
    const text = el.textContent.trim();
    return /^\d+[.)]\s/.test(text);
  };

  const getItemContent = (el) => {
    const clone = el.cloneNode(true);
    clone.querySelectorAll('span').forEach(span => {
      const style = span.getAttribute('style') || '';
      if (style.includes('mso-list') && style.includes('Ignore')) {
        span.remove();
      }
    });
    clone.querySelectorAll('[style]').forEach(child => child.removeAttribute('style'));
    clone.querySelectorAll('[class]').forEach(child => child.removeAttribute('class'));
    clone.querySelectorAll('o\\:p').forEach(child => {
      child.replaceWith(document.createTextNode(child.textContent || ''));
    });
    clone.querySelectorAll('span').forEach(span => {
      if (!span.textContent.trim() && !span.querySelector('img')) span.remove();
      else if (span.attributes.length === 0) span.replaceWith(...span.childNodes);
    });
    return clone.innerHTML.trim();
  };

  const buildNestedList = (items) => {
    if (items.length === 0) return null;

    const buildLevel = (startIdx, targetLevel) => {
      const isOrdered = items[startIdx].ordered;
      const list = document.createElement(isOrdered ? 'ol' : 'ul');
      let i = startIdx;

      while (i < items.length) {
        const item = items[i];
        if (item.level < targetLevel) break;

        if (item.level === targetLevel) {
          const li = document.createElement('li');
          li.innerHTML = item.content;
          list.appendChild(li);
          i++;

          if (i < items.length && items[i].level > targetLevel) {
            const sub = buildLevel(i, items[i].level);
            li.appendChild(sub.list);
            i = sub.nextIndex;
          }
        } else {
          const li = document.createElement('li');
          li.style.listStyleType = 'none';
          const sub = buildLevel(i, item.level);
          li.appendChild(sub.list);
          list.appendChild(li);
          i = sub.nextIndex;
        }
      }

      return { list, nextIndex: i };
    };

    return buildLevel(0, items[0].level).list;
  };

  // Collect and convert Word list groups
  const allChildren = Array.from(temp.children);
  let idx = 0;

  while (idx < allChildren.length) {
    const el = allChildren[idx];

    if (isWordListParagraph(el)) {
      const group = [];
      while (idx < allChildren.length && isWordListParagraph(allChildren[idx])) {
        const p = allChildren[idx];
        group.push({
          level: getListLevel(p),
          ordered: isOrderedItem(p),
          content: getItemContent(p),
          element: p,
        });
        idx++;
      }

      const listEl = buildNestedList(group);
      if (listEl && group[0].element.parentNode) {
        group[0].element.parentNode.insertBefore(listEl, group[0].element);
        group.forEach(item => item.element.remove());
      }
    } else {
      idx++;
    }
  }

  // 5. Clean remaining Word markup
  temp.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'));
  temp.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'));

  // 6. Clean empty/unnecessary spans
  temp.querySelectorAll('span').forEach(el => {
    if (!el.textContent.trim() && !el.querySelector('img')) {
      el.remove();
    } else if (el.attributes.length === 0) {
      el.replaceWith(...el.childNodes);
    }
  });

  // 7. Remove empty paragraphs
  temp.querySelectorAll('p').forEach(el => {
    if (!el.textContent.trim() && !el.querySelector('img')) {
      el.remove();
    }
  });

  return temp.innerHTML;
}

/**
 * TipTap extension for cleaning Word/Office paste content
 */
const PasteHandler = Extension.create({
  name: 'pasteHandler',

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('pasteHandler'),
        props: {
          handlePaste(view, event) {
            const html = event.clipboardData?.getData('text/html');
            if (html && isWordContent(html)) {
              event.preventDefault();
              const cleaned = cleanHtmlForPaste(html);
              editor.commands.insertContent(cleaned);
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});

export default PasteHandler;
